import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import { Sheet } from '../../design/Sheet'
import { IdeaStatusChip, statusLabelKey } from './IdeaStatusChip'
import type { IdeaStatus, MyIdea } from './useIdeas'

/** The road an idea takes. One that is turned down stops at "Not this time". */
export function stepsFor(idea: Pick<MyIdea, 'kind' | 'status'>): IdeaStatus[] {
  if (idea.kind === 'feedback') return ['new', 'read']
  if (idea.status === 'declined') return ['new', 'review', 'declined']
  return ['new', 'review', 'top', 'built']
}

function hintKey(idea: MyIdea, step: IdeaStatus): string {
  if (idea.kind === 'feedback' && step === 'new') return 'ideas.statusHint.feedbackNew'
  return `ideas.statusHint.${step}`
}

interface Props {
  idea: MyIdea | null
  onClose: () => void
  formatDate: (iso: string) => string
}

/** One idea: where it is on its road, and what Paul said about it. */
export function IdeaDetailSheet({ idea, onClose, formatDate }: Props) {
  const { t } = useTranslation()
  const steps = idea ? stepsFor(idea) : []
  const reached = idea ? steps.indexOf(idea.status) : -1

  return (
    <Sheet
      isOpen={idea !== null}
      onClose={onClose}
      aria-label={t('ideas.title')}
      data-testid="idea-detail"
    >
      {idea && (
        <div className="flex flex-col gap-3 overflow-y-auto px-5 pb-5 pt-1">
          <div className="flex">
            <IdeaStatusChip kind={idea.kind} status={idea.status} />
          </div>
          <div>
            <h2 className="text-balance break-words text-lg font-bold leading-snug">{idea.body}</h2>
            <p className="mt-1 text-sm text-foreground/50">
              {idea.kind === 'idea' ? t('ideas.kindIdea') : t('ideas.kindFeedback')} ·{' '}
              {formatDate(idea.createdAt)}
            </p>
          </div>

          {idea.reply && (
            <div className="rounded-lg bg-[color-mix(in_srgb,var(--gym-primary)_12%,transparent)] px-4 py-3 text-sm">
              <span className="mb-0.5 block text-[11px] font-bold uppercase tracking-wider text-[var(--gym-primary)]">
                {t('ideas.replyFrom')}
              </span>
              {idea.reply}
            </div>
          )}

          <ol className="mt-1 flex flex-col" aria-label={t('ideas.title')}>
            {steps.map((step, i) => {
              const done = i <= reached
              const last = i === steps.length - 1
              return (
                <li key={step} className="relative grid grid-cols-[22px_1fr] gap-3 pb-4 last:pb-0">
                  {!last && (
                    <span
                      aria-hidden
                      className={`absolute bottom-0.5 left-[10px] top-6 w-0.5 rounded ${
                        i < reached ? 'bg-[var(--gym-primary)]' : 'bg-foreground/10'
                      }`}
                    />
                  )}
                  <span
                    aria-hidden
                    className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-bold ${
                      done ? 'bg-[var(--gym-primary)] text-white' : 'bg-foreground/10'
                    }`}
                  >
                    {done ? '✓' : ''}
                  </span>
                  <span className={done ? 'text-foreground' : 'text-foreground/40'}>
                    <span className="block text-sm font-semibold">
                      {t(statusLabelKey(idea.kind, step))}
                    </span>
                    <span className="block text-xs text-foreground/50">
                      {t(hintKey(idea, step))}
                    </span>
                  </span>
                </li>
              )
            })}
          </ol>

          <Button
            size="lg"
            radius="lg"
            onPress={onClose}
            className="mt-1 bg-foreground font-bold text-background"
          >
            {t('ideas.done')}
          </Button>
        </div>
      )}
    </Sheet>
  )
}
